import React, { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Ban, CircleCheck, Clock3, UserRound } from "lucide-react";
import { useViewSingleApplicationImage } from "../../hooks/applicationImages/applicationImages.view.single.js";
import { useUpdateApplicationImageStatus } from "../../hooks/applicationImages/applicationImages.updateStatus.js";
import ApplicationImageRejectModal from "../../components/modal/applicationImageRejectModal";
import {
  getApplicationImageStatus,
  getApplicationImageStatusClass,
} from "../../utils/applicationImageStatus";
import { getRejectReason } from "../../utils/applicationImageRejectReason";
import "../../assets/styles/applicationImageDetail.css";
import "../../assets/styles/products.css";
import "../../assets/styles/button.css";
import FallbackImage from "../../components/common/fallbackImage";
import { useViewSubscription } from "../../hooks/subscription/subscription.view";
import {
  getPlanLimitMessage,
  getPlanMediaLimits,
} from "../subscription/planLimits";
import { toast } from "react-hot-toast";

const getGalleryImages = (item) => {
  const gallery = item?.gallery || item?.images || item?.media?.images;
  if (Array.isArray(gallery) && gallery.length > 0) {
    return gallery
      .map((g) => (typeof g === "string" ? g : g?.url || g?.path || g?.image))
      .filter(Boolean);
  }
  const single =
    item?.image || item?.imageUrl || item?.url || item?.logo || item?.filePath;
  return single ? [single] : [];
};

const getApplicantName = (item) => {
  return (
    item?.user?.name ||
    item?.user?.fullName ||
    item?.applicantName ||
    item?.applicant?.name ||
    item?.userName ||
    item?.createdBy?.name ||
    item?.email ||
    "-"
  );
};

const ViewApplicationImage = () => {
  const { id } = useParams();
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);

  const {
    data: rawData,
    isLoading,
    isError,
  } = useViewSingleApplicationImage(id);
  const { mutate: updateStatus, isPending: isUpdatingStatus } =
    useUpdateApplicationImageStatus();
  const { data: subscriptionResponse, isPlaceholderData } =
    useViewSubscription();
  const planLimits = getPlanMediaLimits(subscriptionResponse, {
    isReady: !isPlaceholderData,
  });

  const applicationImage = useMemo(() => {
    if (!rawData) return null;
    return rawData?.data || rawData;
  }, [rawData]);

  const galleryUrls = useMemo(() => {
    if (!applicationImage) return [];
    return getGalleryImages(applicationImage).filter(Boolean);
  }, [applicationImage]);

  const mainImage = galleryUrls[selectedIndex] || galleryUrls[0] || null;

  const status = applicationImage
    ? getApplicationImageStatus(applicationImage)
    : "pending";
  const statusClass = getApplicationImageStatusClass(status);
  const statusLabel = (applicationImage?.status || status)
    .toString()
    .toUpperCase();
  const title =
    applicationImage?.title || applicationImage?.name || "Application Detail";
  const industryTitle =
    applicationImage?.industry?.title ||
    (typeof applicationImage?.industry === "string"
      ? applicationImage.industry
      : "-");

  const brandTitle =
    applicationImage?.brand?.title ||
    (typeof applicationImage?.brand === "string"
      ? applicationImage.brand
      : "-");

  const subIndustryTitle =
    applicationImage?.subIndustry?.title ||
    (typeof applicationImage?.subIndustry === "string"
      ? applicationImage.subIndustry
      : "-");

  const products = applicationImage?.products || [];

  const handleApprove = () => {
    if (!id) return;
    if (!planLimits.applicationImages?.canApprove) {
      toast.error(getPlanLimitMessage("applicationImages", planLimits));
      return;
    }
    updateStatus({ id, status: "approved" });
  };

  const handleSetPending = () => {
    if (!id) return;
    updateStatus({ id, status: "pending" });
  };

  const handleRejectConfirm = (rejectReason) => {
    if (!id) return;
    updateStatus(
      { id, status: "rejected", rejectReason },
      { onSettled: () => setIsRejectModalOpen(false) },
    );
  };

  const rejectReasonText = applicationImage
    ? getRejectReason(applicationImage)
    : "";

  if (isLoading) {
    return (
      <div className="application-detail-page">
        <p className="application-detail-loading">
          Loading application details...
        </p>
      </div>
    );
  }

  if (isError || !applicationImage) {
    return (
      <div className="application-detail-page">
        <p className="application-detail-error">
          Unable to load application details.
        </p>
        <Link to="/applicationImages">Back to Application Images</Link>
      </div>
    );
  }

  return (
    <div className="application-detail-page">
      <nav className="application-detail-breadcrumb">
        <Link to="/applicationImages">Application Images</Link>
        <span> &gt; </span>
        <span className="current">Application Detail</span>
      </nav>

      <header className="application-detail-header">
        <div className="application-detail-title-row">
          <h1 className="application-detail-title">{title}</h1>
          <span className={`status-badge ${statusClass}`}>{statusLabel}</span>
        </div>

        <div className="application-detail-actions">
          <button
            type="button"
            className="application-detail-btn application-detail-btn--edit"
            disabled={isUpdatingStatus || status === "pending"}
            onClick={handleSetPending}
          >
            <Clock3 size={18} />
            Pending
          </button>
          <button
            type="button"
            className="application-detail-btn application-detail-btn--reject"
            disabled={isUpdatingStatus || status === "rejected"}
            onClick={() => setIsRejectModalOpen(true)}
          >
            <Ban size={18} />
            Reject
          </button>
          <button
            type="button"
            className="application-detail-btn application-detail-btn--approve"
            disabled={
              isUpdatingStatus ||
              status === "approved" ||
              !planLimits.applicationImages?.canApprove
            }
            onClick={handleApprove}
          >
            <CircleCheck size={18} />
            Approve
          </button>
        </div>
      </header>
      <p
        className={`plan-limit-hint${
          planLimits.applicationImages?.canApprove ? "" : " is-limit"
        }`}
        style={{ margin: "0 0 16px" }}
      >
        {getPlanLimitMessage("applicationImages", planLimits)}
      </p>

      <div className="application-detail-content">
        <div className="application-detail-gallery-card">
          {mainImage ? (
            <FallbackImage
              src={mainImage}
              alt={title}
              className="application-detail-main-image"
            />
          ) : (
            <div className="application-detail-no-image">
              No image available
            </div>
          )}

          {galleryUrls.length > 0 && (
            <div className="application-detail-thumbs">
              {galleryUrls.map((url, index) => (
                <FallbackImage
                  key={`${url}-${index}`}
                  src={url}
                  alt={`Gallery ${index + 1}`}
                  className={`application-detail-thumb${
                    index === selectedIndex ? " active" : ""
                  }`}
                  onClick={() => setSelectedIndex(index)}
                />
              ))}
            </div>
          )}
        </div>

        <aside className="application-detail-sidebar">
          <div className="application-detail-info-card">
            <div className="application-detail-info-card__header">
              <h2 className="application-detail-info-card__title">
                USER INFORMATION
              </h2>
              <span className="application-detail-info-card__icon">
                <UserRound size={20} />
              </span>
            </div>
            <div className="application-detail-field">
              <span className="application-detail-field__label">
                Applicant Name
              </span>
              <p className="application-detail-field__value">
                {getApplicantName(applicationImage)}
              </p>
            </div>
          </div>

          <div className="application-detail-info-card">
            <div className="application-detail-info-card__header">
              <h2 className="application-detail-info-card__title">
                APPLICATION DETAILS
              </h2>
            </div>
            <div className="application-detail-field">
              <span className="application-detail-info-card__title">
                Industry
              </span>
              <p className="application-detail-field__value">{industryTitle}</p>
            </div>
            <div className="application-detail-field">
              <span className="application-detail-info-card__title">
                Sub-Industry
              </span>
              <p className="application-detail-field__value">
                {subIndustryTitle}
              </p>
            </div>
            <div className="application-detail-field">
              <span className="application-detail-info-card__title">Brand</span>
              <p className="application-detail-field__value">{brandTitle}</p>
            </div>
            {products.length > 0 && (
              <div className="application-detail-field">
                <span className="application-detail-info-card__title">
                  Products
                </span>
                <ul className="application-detail-products">
                  {products.map((product) => (
                    <li key={product._id || product.id || product.title}>
                      {product.title || product.name || "-"}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
          {status === "rejected" && rejectReasonText && (
            <div className="application-detail-info-card application-detail-reject-card">
              <div className="application-detail-info-card__header">
                <h2 className="application-detail-info-card__title">
                  REJECTION REASON
                </h2>
              </div>
              <p className="application-detail-reject-reason">
                {rejectReasonText}
              </p>
            </div>
          )}
        </aside>
      </div>

      <ApplicationImageRejectModal
        isOpen={isRejectModalOpen}
        onClose={() => setIsRejectModalOpen(false)}
        image={applicationImage}
        onConfirm={handleRejectConfirm}
        isLoading={isUpdatingStatus}
      />
    </div>
  );
};

export default ViewApplicationImage;
